/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';

import { AuthProvider } from '@app/auth';
import RoleGate, { ADMIN_ROLES, SUPERADMIN_ROLES } from '@shared/auth/RoleGate';
import ErrorBoundary from '@shared/components/ErrorBoundary';
import Layout from '@shared/components/Layout';
import ProtectedRoute from '@shared/components/ProtectedRoute';
import usePermissions from '@shared/hooks/usePermissions';

/**
 * Landing por defecto al entrar a "/". El dashboard ya no es accesible para
 * todos los roles (solo superadmin), así que redirigimos según el rol:
 * superadmin → dashboard, resto → inmuebles (página universal con permisos).
 */
function HomeRedirect() {
  const { isSuperAdmin } = usePermissions();
  return <Navigate to={isSuperAdmin ? '/dashboard-metricas' : '/inmuebles'} replace />;
}

const LoginPage = lazy(() => import('@modules/auth/pages/LoginPage'));

const DashboardPage = lazy(() => import('@modules/dashboard/pages/DashboardPage'));
const DashboardMetricasPage = lazy(() => import('@modules/dashboard/pages/DashboardMetricasPage'));

const UsersPage = lazy(() => import('@modules/users/pages/UsersPage'));

const LeadsOficinaPage = lazy(() => import('@modules/leads/pages/LeadsOficinaPage'));
const LeadsDigitalesPage = lazy(() => import('@modules/leads/pages/LeadsDigitalesPage'));

const PropertiesListPage = lazy(() => import('@modules/properties/pages/PropertiesListPage'));
const PropertiesRentalPage = lazy(() => import('@modules/properties/pages/PropertiesRentalPage'));
const PropertiesSalePage = lazy(() => import('@modules/properties/pages/PropertiesSalePage'));

const OperationsPage = lazy(() => import('@modules/operations/pages/OperationsPage'));

const ProspeccionAgentesPage = lazy(
  () => import('@modules/prospeccion/pages/ProspeccionAgentesPage')
);

const SuministrosPage = lazy(() => import('@modules/marketing/pages/SuministrosPage'));
const SegurosPage = lazy(() => import('@modules/marketing/pages/SegurosPage'));

const AvisosPage = lazy(() => import('@modules/avisos/pages/AvisosPage'));

const InmovillaSyncDashboardPage = lazy(
  () => import('@modules/integrations/pages/InmovillaSyncDashboardPage')
);
const InmovillaCatalogMapPage = lazy(
  () => import('@modules/integrations/pages/InmovillaCatalogMapPage')
);
const InmovillaUserMapPage = lazy(() => import('@modules/integrations/pages/InmovillaUserMapPage'));

function PageFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center justify-center py-24 space-y-4"
    >
      <div className="w-12 h-12 border-4 border-brand-tomato/20 border-t-brand-tomato rounded-full animate-spin" />
      <p className="text-brand-gray font-medium">Cargando…</p>
    </div>
  );
}

function RouteShell({ name }: { name: string }) {
  return (
    <ErrorBoundary name={name}>
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  );
}

/** Root que monta AuthProvider dentro del router (useNavigate lo requiere). */
function RootLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/login',
        element: (
          <ErrorBoundary name="login">
            <Suspense fallback={<PageFallback />}>
              <LoginPage />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <Layout>
              <ErrorBoundary name="layout">
                <Suspense fallback={<PageFallback />}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </Layout>
          </ProtectedRoute>
        ),
        children: [
          { path: '/', element: <HomeRedirect /> },

          {
            path: 'dashboard',
            element: <RouteShell name="dashboard" />,
            children: [
              {
                index: true,
                element: (
                  <RoleGate allowedRoles={SUPERADMIN_ROLES}>
                    <DashboardPage />
                  </RoleGate>
                ),
              },
            ],
          },
          {
            path: 'dashboard-metricas',
            element: <RouteShell name="dashboard-metricas" />,
            children: [
              {
                index: true,
                element: (
                  <RoleGate allowedRoles={SUPERADMIN_ROLES}>
                    <DashboardMetricasPage />
                  </RoleGate>
                ),
              },
            ],
          },

          {
            path: 'users',
            element: <RouteShell name="users" />,
            children: [
              {
                index: true,
                element: (
                  <RoleGate allowedRoles={ADMIN_ROLES}>
                    <UsersPage />
                  </RoleGate>
                ),
              },
            ],
          },

          {
            path: 'leads-oficina',
            element: <RouteShell name="leads-oficina" />,
            children: [{ index: true, element: <LeadsOficinaPage /> }],
          },
          {
            path: 'leads-digitales',
            element: <RouteShell name="leads-digitales" />,
            children: [{ index: true, element: <LeadsDigitalesPage /> }],
          },

          {
            path: 'inmuebles',
            element: <RouteShell name="inmuebles" />,
            children: [{ index: true, element: <PropertiesListPage /> }],
          },
          {
            path: 'inmuebles-alquiler',
            element: <RouteShell name="inmuebles-alquiler" />,
            children: [{ index: true, element: <PropertiesRentalPage /> }],
          },
          {
            path: 'inmuebles-venta',
            element: <RouteShell name="inmuebles-venta" />,
            children: [{ index: true, element: <PropertiesSalePage /> }],
          },

          {
            path: 'prospeccion-agentes',
            element: <RouteShell name="prospeccion-agentes" />,
            children: [{ index: true, element: <ProspeccionAgentesPage /> }],
          },

          {
            path: 'operaciones',
            element: <RouteShell name="operaciones" />,
            children: [{ index: true, element: <OperationsPage /> }],
          },

          {
            path: 'cambio-suministros',
            element: <RouteShell name="cambio-suministros" />,
            children: [{ index: true, element: <SuministrosPage /> }],
          },
          {
            path: 'seguro-alquiler',
            element: <RouteShell name="seguro-alquiler" />,
            children: [{ index: true, element: <SegurosPage /> }],
          },

          {
            path: 'avisos',
            element: <RouteShell name="avisos" />,
            children: [{ index: true, element: <AvisosPage /> }],
          },

          {
            path: 'admin/inmovilla-sync',
            element: <RouteShell name="inmovilla-sync" />,
            children: [
              {
                index: true,
                element: (
                  <RoleGate allowedRoles={ADMIN_ROLES}>
                    <InmovillaSyncDashboardPage />
                  </RoleGate>
                ),
              },
            ],
          },
          {
            path: 'admin/inmovilla-catalog-map',
            element: <RouteShell name="inmovilla-catalog-map" />,
            children: [
              {
                index: true,
                element: (
                  <RoleGate allowedRoles={ADMIN_ROLES}>
                    <InmovillaCatalogMapPage />
                  </RoleGate>
                ),
              },
            ],
          },
          {
            path: 'admin/inmovilla-user-map',
            element: <RouteShell name="inmovilla-user-map" />,
            children: [
              {
                index: true,
                element: (
                  <RoleGate allowedRoles={ADMIN_ROLES}>
                    <InmovillaUserMapPage />
                  </RoleGate>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
]);

export default router;
